import $ from 'jquery';
import d3 from 'd3';
import { sanitizeHtml } from './utils';

export default function () {
  let height, margin, parentContainerElement, width;
  const chart = selection =>
    selection.each(function (data) {
      const adjustedUnits = units => units / (data.base_units || 1);

      const oneDay = 24 * 60 * 60 * 1000;

      const containerElement = this;
      const containerRect = containerElement.getBoundingClientRect();
      const elementWidth = containerRect.width;

      const miniChart = elementWidth < 300;
      margin =
        miniChart || data.panel
          ? { top: 10, right: 10, bottom: 20, left: 40 }
          : { top: 20, right: 80, bottom: 30, left: 50 };

      if (data.panel) {
        parentContainerElement = $(containerElement).parents(
          'div[class^="PanelContent-"]'
        );
      } else {
        parentContainerElement = $('body');
      }

      const availableHeight =
        parentContainerElement.height() -
        containerRect.top -
        margin.top -
        margin.bottom;

      if (data.panel) {
        // This is specifically for the panel view on dashboards, we multiply by 3 to give compensate for padding
        // and the margins at the top and bottom
        height =
          parentContainerElement.height() - 3 * (margin.top + margin.bottom);
        width = elementWidth - margin.left - margin.right;
      } else if (availableHeight < 500) {
        // not enough height to show a useful graph, use the full width to determine the height
        width = elementWidth - margin.left - margin.right;
        height = elementWidth * 0.6 - margin.top - margin.bottom;
      } else {
        // plenty of height, use the height to determine the width
        height = availableHeight - margin.top - margin.bottom;
        width = (availableHeight * 5) / 3 - margin.left - margin.right;
      }

      // Convert dates to d3 format.
      const parseDate = d3.time.format('%Y-%m-%d').parse;
      const spansYear =
        data.chart_from.split('-')[0] !== data.chart_to.split('-')[0];

      data.chart_from = parseDate(data.chart_from);
      data.chart_to = parseDate(data.chart_to);
      data.release_date = parseDate(data.release_date);
      data.history.forEach(d => (d.date = parseDate(d.date)));

      const x = d3.time.scale().range([0, width]);
      const y = d3.scale.linear().range([height, 0]);

      const xAxis = d3.svg
        .axis()
        .tickFormat(
          d3.time.format.multi([
            ['%b %Y', d => spansYear && d.getMonth() === 0],
            ['%b', () => spansYear],
            ['%b %d', () => true],
          ])
        )
        .scale(x)
        .orient('bottom');

      if (miniChart) {
        xAxis.ticks(5);
      }

      const yAxis = d3.svg.axis().scale(y).orient('left');

      const startingWork = adjustedUnits(data.starting_work);
      const maxWork = d3.max(data.history, d =>
        adjustedUnits(Math.max(d.value, d.added))
      );

      x.domain([data.chart_from, d3.time.day.offset(data.chart_to, 1)]);
      y.domain([0, maxWork * 1.2]);

      // Compute the number of workdays.
      let workDays = 0;
      let date = data.chart_from;
      while (date < data.release_date) {
        if (
          d3.time.saturday(date) - date !== 0 &&
          d3.time.sunday(date) - date !== 0
        ) {
          workDays++;
        }
        date = d3.time.day.offset(date, 1);
      }
      const workPerDay = startingWork / workDays;
      // Compute the ideal line data.
      let work = startingWork;
      let lastDate = data.chart_from;
      date = data.chart_from;
      const idealData = [[lastDate, work]];
      while (date < data.release_date) {
        if (d3.time.monday(date) - date === 0) {
          idealData.push([date, work]);
          lastDate = date;
        } else if (d3.time.saturday(date) - date === 0) {
          const days = (date - lastDate) / oneDay;
          work -= workPerDay * days;
          idealData.push([date, work]);
        }
        date = d3.time.day.offset(date, 1);
      }
      idealData.push([date, 0]);

      const line = d3.svg
        .line()
        .interpolate('step-after')
        .x(d => x(d3.time.hour.offset(d.date, 12)))
        .y(d => y(adjustedUnits(d.value)));

      const idealLine = d3.svg
        .line()
        .interpolate('linear')
        .x(d => x(d[0]))
        .y(d => y(d[1]));

      const svg = d3.select(this).selectAll('svg').data([data]);
      const svgEnter = svg
        .enter()
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom);

      const chartEnter = svgEnter
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      var tooltip = d3
        .select(`#${containerElement.id}`)
        .append('div')
        .attr('class', 'burndown-tooltip')
        .style('opacity', 0)
        .on('mouseover', () =>
          tooltip.transition().duration(200).style('opacity', 0.9)
        )
        .on('mouseout', () => hideTooltip());

      svgEnter
        .append('defs')
        .append('clipPath')
        .attr('id', 'progress-clip')
        .append('rect')
        .attr('width', width)
        .attr('height', height);

      chartEnter
        .append('g')
        .attr('class', 'x axis')
        .attr('transform', `translate(0,${height})`)
        .call(xAxis);
      let y_axis_name = '';
      if (data.y_axis) {
        y_axis_name = data.y_axis.name;
      }
      chartEnter
        .append('g')
        .attr('class', 'y axis')
        .call(yAxis)
        .append('text')
        .attr('transform', 'rotate(-90)')
        .attr('y', 6)
        .attr('dy', '.71em')
        .style('text-anchor', 'end')
        .text(y_axis_name);

      const contentEnter = chartEnter
        .append('g')
        .attr('clip-path', 'url(#progress-clip)');

      contentEnter
        .selectAll('.today')
        .data([
          [
            [new Date(), 0],
            [new Date(), y.domain()[1]],
          ],
        ])
        .enter()
        .append('path')
        .attr('class', 'today')
        .attr(
          'd',
          d3.svg
            .line()
            .x(d => x(d[0]))
            .y(d => y(d[1]))
        );

      contentEnter
        .selectAll('.weekend')
        .data(x.ticks(d3.time.weeks))
        .enter()
        .append('rect')
        .attr('class', 'weekend')
        .attr('width', () => x(oneDay * 2) - x(0))
        .attr('height', height)
        .attr('x', d => x(d - oneDay))
        .attr('y', () => y(y.domain()[1]));

      contentEnter
        .append('path')
        .attr('class', 'burndown-line')
        .attr('d', d => line(d.history));

      contentEnter
        .append('path')
        .data([idealData])
        .attr('class', 'ideal-line')
        .attr('d', d => idealLine(d));

      const showTooltip = function (element, title, items) {
        const itemsHtml = $.map(
          items,
          item =>
            `<li><a href='#charts' data-drawer-url='${item.url}'>${item.title}</a></li>`
        );
        tooltip.transition().duration(200).style('opacity', 0.9);

        // DO NOT REPLICATE: unsafe due to title and item interpolation
        return tooltip
          .html(sanitizeHtml(`<h4>${title}</h4><ul>${itemsHtml.join('')}</ul>`))
          .style('left', function () {
            const xPos = parseInt(d3.select(element).attr('x'));
            const columnWidth = x(oneDay) - x(0);
            const offset =
              xPos > $(containerElement).width() / 2
                ? -(300 + columnWidth)
                : columnWidth;

            return xPos + offset + margin.left + 'px';
          })
          .style(
            'top',
            parseInt(d3.select(element).attr('y')) + margin.top + 'px'
          );
      };

      var hideTooltip = () =>
        tooltip
          .transition()
          .duration(500)
          .style('opacity', 0)
          .each('end', () => tooltip.style('left', '-1000px'));

      if (!miniChart) {
        contentEnter
          .selectAll('.work-done')
          .data(d => d.history)
          .enter()
          .append('rect')
          .attr('class', 'work-done')
          .attr('x', d => x(d3.time.hour.offset(d.date, 6)))
          .attr('y', d =>
            y(adjustedUnits(d.value - (-d.done + d.added - d.removed)))
          )
          .attr('width', () => x(oneDay / 2) - x(0))
          .attr('height', d => y(0) - y(adjustedUnits(d.done)))
          .on('mouseover', function (d) {
            return showTooltip(this, 'Work done', d.items_done);
          })
          .on('mouseout', function () {
            return hideTooltip();
          });
        contentEnter
          .selectAll('.scope-added')
          .data(d => d.history)
          .enter()
          .append('rect')
          .attr('class', 'scope-added')
          .attr('x', d => x(d3.time.hour.offset(d.date, 6)))
          .attr('y', d =>
            y(adjustedUnits(d.value + d.added + (d.done + d.removed) - d.added))
          )
          .attr('width', () => x(oneDay / 2) - x(0))
          .attr('height', d => y(0) - y(adjustedUnits(d.added)))
          .on('mouseover', function (d) {
            return showTooltip(this, 'Scope increased', d.items_added);
          })
          .on('mouseout', function () {
            return hideTooltip();
          });
        return contentEnter
          .selectAll('.scope-removed')
          .data(d => d.history)
          .enter()
          .append('rect')
          .attr('class', 'scope-removed')
          .attr('x', d => x(d3.time.hour.offset(d.date, 6)))
          .attr('y', d => y(adjustedUnits(d.value - (d.added - d.removed))))
          .attr('width', () => x(oneDay / 2) - x(0))
          .attr('height', d => y(0) - y(adjustedUnits(d.removed)))
          .on('mouseover', function (d) {
            return showTooltip(this, 'Scope reduced', d.items_removed);
          })
          .on('mouseout', function () {
            return hideTooltip();
          });
      }
    });

  chart.width = function (_) {
    if (!arguments.length && width) {
      return width;
    }
    width = _;
    return chart;
  };

  return chart;
}
